import { useTranslations } from "next-intl";

import * as Dialog from "@/components/dialog/DialogLayout";
import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import type { IconName } from "@/components/icons/Icon";
import type { GridAction, GridAxis } from "@/components/tiptap/grid-controls/gridTarget";

type Props = {
  open: boolean;
  /** Kept while closing so the sheet's content doesn't change mid-animation. */
  axis: GridAxis;
  onClose: () => void;
  onAction: (action: GridAction) => void;
};

const ACTIONS: { action: GridAction; icon: Record<GridAxis, IconName>; destructive?: boolean }[] = [
  { action: "insertBefore", icon: { row: "RowInsertAbove", col: "ColumnInsertLeft" } },
  { action: "insertAfter", icon: { row: "RowInsertBelow", col: "ColumnInsertRight" } },
  { action: "duplicate", icon: { row: "Duplicate", col: "Duplicate" } },
  { action: "copy", icon: { row: "Copy", col: "Copy" } },
  { action: "clear", icon: { row: "ClearEntries", col: "ClearEntries" } },
  { action: "remove", icon: { row: "Trash", col: "Trash" }, destructive: true },
];

/**
 * Row/column options for the table, matrix or cases cell whose handle was
 * tapped — the browser twin of the mobile app's native GridMenuSheet.
 */
export function GridMenuSheet({ open, axis, onClose, onAction }: Props) {
  const t = useTranslations("gridMenu");

  return (
    // The sheet portals out of the editor's DOM but still bubbles through its React
    // tree, whose click handler resets the math input.
    <Dialog.Root open={open} onClose={onClose} onClick={(e) => e.stopPropagation()}>
      <Dialog.Backdrop
        transition
        className="transition-opacity duration-200 data-closed:opacity-0"
      />
      <div className="fixed inset-x-0 bottom-0 flex justify-center">
        <Dialog.Panel
          transition
          className={twMerge(
            "w-full max-w-[560px] max-h-dvh p-0 rounded-b-none rounded-t-[20px]",
            "pb-[env(safe-area-inset-bottom)]",
            "transition-transform duration-200 ease-out data-closed:translate-y-full",
          )}
        >
          <Dialog.Title className="px-4 pt-4 pb-3 text-[20px] leading-[24px] font-medium">
            {t(`${axis}Title`)}
          </Dialog.Title>
          <Dialog.CloseButton>
            <Dialog.CloseIcon />
          </Dialog.CloseButton>
          {/* Scrolls on a short landscape screen, where the actions don't all fit. */}
          <div className="flex flex-col min-h-0 overflow-y-auto divide-y divide-black/10 dark:divide-white/10 border-t border-black/10 dark:border-white/10">
            {ACTIONS.map(({ action, icon, destructive }) => (
              <button
                key={action}
                type="button"
                className={twMerge(
                  "flex items-center justify-between h-[52px] px-4 text-[17px] text-left",
                  "active:bg-black/5 dark:active:bg-white/10",
                  destructive && "text-red-600",
                )}
                onClick={() => {
                  onClose();
                  onAction(action);
                }}
              >
                {t(`${axis}.${action}`)}
                <Icon name={icon[axis]} className="w-6 h-6" />
              </button>
            ))}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog.Root>
  );
}
